// =============================================================================
// GraphQL Mutations — ResolveGuru AppSync
// Only incident-related mutations are active for Phase 1 (IncidentView page)
// =============================================================================

export const classify = /* GraphQL */ `
  mutation Classify($input: ClassifyInput!) {
    classify(input: $input) {
      statusCode
      body
    }
  }
`;

export const classifyTribe = /* GraphQL */ `
  mutation ClassifyTribe($input: ClassifyTribeInput!) {
    classifyTribe(input: $input) {
      statusCode
      body
    }
  }
`;

export const createIncident = /* GraphQL */ `
  mutation CreateIncident($input: CreateIncidentInput!) {
    createIncident(input: $input) {
      incident_number
      datetime
    }
  }
`;

export const updateIncident = /* GraphQL */ `
  mutation UpdateIncident(
    $incident_number: String!
    $datetime: String!
    $input: UpdateIncidentInput!
  ) {
    updateIncident(
      incident_number: $incident_number
      datetime: $datetime
      input: $input
    ) {
      incident_number
      datetime
      overall_correctness
      overall_text_feedback
      step_feedback
      modified_by
      modified_at
    }
  }
`;

export const deleteIncident = /* GraphQL */ `
  mutation DeleteIncident($incident_number: String!, $datetime: String!) {
    deleteIncident(incident_number: $incident_number, datetime: $datetime) {
      incident_number
      datetime
    }
  }
`;

export const refreshImageUrls = /* GraphQL */ `
  mutation RefreshImageUrls($input: RefreshImageUrlsInput!) {
    refreshImageUrls(input: $input) {
      statusCode
      body
    }
  }
`;

export const requestRunbook = /* GraphQL */ `
  mutation RequestRunbook($input: RequestRunbookInput!) {
    requestRunbook(input: $input) {
      statusCode
      body
    }
  }
`;

// =============================================================================
// Prod Performance Mutations
// =============================================================================

export const analyzeProdPerformance = /* GraphQL */ `
  mutation AnalyzeProdPerformance($input: AnalyzeProdPerformanceInput!) {
    analyzeProdPerformance(input: $input) {
      statusCode
      body
    }
  }
`;

// =============================================================================
// Tribe Performance Mutations
// =============================================================================

export const analyzeTribePerformance = /* GraphQL */ `
  mutation AnalyzeTribePerformance($input: AnalyzeTribePerformanceInput!) {
    analyzeTribePerformance(input: $input) {
      statusCode
      body
    }
  }
`;

export const getTribeSummary = /* GraphQL */ `
  mutation GenerateTribeSummary($input: GenerateTribeSummaryInput!) {
    generateTribeSummary(input: $input) {
      statusCode
      body
    }
  }
`;

// =============================================================================
// DevOps Agent Mutations
// =============================================================================

export const triggerDevopsAgent = /* GraphQL */ `
  mutation TriggerAgent($alarmName: String!, $incidentNumber: String, $forceRefresh: Boolean) {
    triggerDevopsAgent(alarmName: $alarmName, incidentNumber: $incidentNumber, forceRefresh: $forceRefresh) {
      sessionId
      status
      message
      incident_number
      incident_datetime
    }
  }
`;

export const triggerDevopsInvestigation = /* GraphQL */ `
  mutation TriggerDevopsInvestigation($input: TriggerDevopsInvestigationInput!) {
    triggerDevopsInvestigation(input: $input) {
      sessionId
      status
      message
      incident_number
      incident_datetime
    }
  }
`;

export const submitDevopsAgentFeedback = /* GraphQL */ `
  mutation SubmitDevopsAgentFeedback($input: SubmitDevopsAgentFeedbackInput!) {
    submitDevopsAgentFeedback(input: $input) {
      incident_number
      datetime
      message
    }
  }
`;

// Records an operator's approve/reject decision for an investigation plan.
// decision is "ALLOW" (approve) or "DISCARD" (reject); reason is optional.
// Returns the created SessionEntry. data is AWSJSON — JSON.parse() after receiving.
export const postApproval = /* GraphQL */ `
  mutation PostApproval($input: PostApprovalInput!) {
    postApproval(input: $input) {
      sessionId
      sortKey
      entryType
      data
      agentId
      status
      createdAt
      ttl
    }
  }
`;

// =============================================================================
// Plan/Agent invocation via AgentCore
// InvokeAgentInput now supports either:
//   1) short_description + tribe + payload (e.g. orchestrator with S3 path), or
//   2) short_description + tribe + description (when no payload is provided).
// `payload` is optional in the backend schema.
// =============================================================================

export const invokeAgent = /* GraphQL */ `
  mutation InvokeAgent($input: InvokeAgentInput!) {
    invokeAgent(input: $input) {
      sessionId
      status
      error
      agent_name
      hosted_platform
    }
  }
`;

// =============================================================================
// Runbook Mutations (Phase 2: AppSync Migration)
// =============================================================================

export const updateRunbook = /* GraphQL */ `
  mutation UpdateRunbook($keyword: String!, $input: UpdateRunbookInput!) {
    updateRunbook(keyword: $keyword, input: $input) {
      keyword
      tribe
      is_automation
      modified_by
      modified_at
      runbook_steps {
        step_number
        description
        image_s3_key
      }
    }
  }
`;

export const uploadStepImage = /* GraphQL */ `
  mutation UploadStepImage($input: UploadStepImageInput!) {
    uploadStepImage(input: $input) {
      message
      imageUrl
      s3Key
      keyword
      stepNumber
    }
  }
`;

export const deleteStepImage = /* GraphQL */ `
  mutation DeleteStepImage($input: DeleteStepImageInput!) {
    deleteStepImage(input: $input) {
      message
      keyword
      stepNumber
    }
  }
`;

export const generateImageUploadUrl = /* GraphQL */ `
  mutation GenerateImageUploadUrl($input: GenerateImageUploadUrlInput!) {
    generateImageUploadUrl(input: $input) {
      uploadUrl
      viewUrl
      s3Key
      contentType
      keyword
      stepNumber
    }
  }
`;

export const confirmImageUpload = /* GraphQL */ `
  mutation ConfirmImageUpload($input: ConfirmImageUploadInput!) {
    confirmImageUpload(input: $input) {
      message
      imageUrl
      s3Key
      keyword
      stepNumber
    }
  }
`;

// =============================================================================
// Orchestration Agent Feedback Mutations
// =============================================================================

export const submitAgentFeedback = /* GraphQL */ `
  mutation SubmitAgentFeedback($input: SubmitOrchestrationAgentFeedbackInput!) {
    submitAgentFeedback(input: $input) {
      session_id
      agent_id
      feedback
      written_feedback
      status
      error
      agent_name
      user_id
      created_at
      updated_at
    }
  }
`;
